import { makeAutoObservable, runInAction } from 'mobx';
import { $api } from '../http';
import { ICart, ICartItem, IProduct } from '../types/entities';
import { updateQuantity } from '../http/cartAPI';

// EN: Use a more specific type for guest cart storage to avoid saving stale product data.
// PL: Użyj bardziej szczegółowego typu do przechowywania koszyka gościa, aby uniknąć zapisywania nieaktualnych danych o produktach.
type GuestCartStorageItem = { 
    productId: number; 
    quantity: number 
    priceOptionVolume?: string | null;
};

const GUEST_CART_KEY = 'guestCartItems';

export default class CartStore {
    _cart: ICart | null = null;
    _isLoading = false;
    _error: string | null = null;
    isOpen = false;

    constructor() {
        makeAutoObservable(this);
    }

    private isSameCartItem(
        item: ICartItem,
        productId: number,
        priceOptionVolume?: string | null
    ): boolean {
        return (
            item.productId === productId &&
            (item.priceOptionVolume ?? null) ===
                (priceOptionVolume ?? null)
        );
    }

    // --- Private Getters ---
    private get isAuth(): boolean {
        return !!localStorage.getItem('token');
    }

    private getEffectivePrice(
        product: IProduct,
        priceOptionVolume?: string | null
    ): number {
        const priceOption = product.priceOptions?.find(
            option => option.volume === (priceOptionVolume ?? null)
        );
    
        if (priceOption) {
            if (
                priceOption.discountPrice != null &&
                priceOption.discountPrice > 0
            ) {
                return priceOption.discountPrice;
            }
    
            return priceOption.price;
        }
    
        if (
            product.discountPrice != null &&
            product.discountPrice > 0
        ) {
            return product.discountPrice;
        }
    
        return product.price;
    }

    // Getters
    get cartProducts(): ICartItem[] {
      return this._cart?.cartProducts || [];
    }

    get totalQuantity(): number {
      return this.cartProducts.reduce((sum, item) => sum + item.quantity, 0);
    }


    get totalPrice(): number {
        return this.cartProducts.reduce((sum, item) => {
            const price = this.getEffectivePrice(
                item.product,
                item.priceOptionVolume
            );
    
            return sum + price * item.quantity;
        }, 0);
    }

    get isLoading() {
        return this._isLoading;
    }

    get error() {
        return this._error;
    }

    // Actions
    setCart(cart: ICart | null) {
      this._cart = cart;
    }

    setLoading(loading: boolean) {
        this._isLoading = loading;
    }

    setError(error: string | null) {
        this._error = error;
    }

    setIsOpen(bool: boolean) {
        this.isOpen = bool;
    }

    // --- Guest Cart Specific Logic ---

    async loadGuestCart() {
        const guestCartJson = localStorage.getItem(GUEST_CART_KEY);
      if (guestCartJson) {
          try {
            const guestCartItems: GuestCartStorageItem[] = JSON.parse(guestCartJson);
            if (guestCartItems.length > 0) {
              // EN: Fetch fresh product data for items in the guest cart.
              // PL: Pobierz świeże dane o produktach dla przedmiotów w koszyku gościa.
              await this.fetchGuestCartProducts(guestCartItems);
            } else {
              this.setCart(null);
            }
          } catch (e) {
              console.error("Failed to parse guest cart from localStorage", e);
              localStorage.removeItem(GUEST_CART_KEY);
          }
      }
    }

    private async fetchGuestCartProducts(items: GuestCartStorageItem[]) {
        this.setLoading(true);
        try {
            const productIds = items.map(i => i.productId);
            // EN: This requires the backend to support fetching multiple products by a list of IDs
            // PL: Wymaga to od backendu obsługi pobierania wielu produktów po liście ID
            const { data } = await $api.get<{ rows: IProduct[] }>('/products', {
                params: { ids: productIds.join(',') }
            });
    
            const productsById = new Map(data.rows.map(p => [p.id, p]));
    
            const cartProducts = items
                .map((item): ICartItem | null => {
                    const product = productsById.get(item.productId);

                    if (!product) { 
                        return null; // Handle case where product might have been deleted
                    }
                    const priceOption = product.priceOptions?.find(
                        option => option.volume === (item.priceOptionVolume ?? null)
                    );
            
                    const price = this.getEffectivePrice(
                        product,
                        item.priceOptionVolume
                    );
            
                    return {
                        id: item.productId,
                        quantity: item.quantity,
                        cartId: 0,
                        productId: item.productId,
                        priceOptionVolume: item.priceOptionVolume ?? null,
                        product,
                        price,
                    };
                })
                .filter((item): item is ICartItem => item !== null);
    
            runInAction(() => {
                this.setCart({ id: 0, userId: 0, cartProducts });
            });
    
        } catch (e) {
            console.error("Failed to fetch products for guest cart", e);
            this.clearCart(); // Clear broken cart
        } finally {
            this.setLoading(false);
        }
    }

    private saveGuestCart() {
      if (this._cart) {

        const itemsToSave: GuestCartStorageItem[] = this.cartProducts.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            ...(item.priceOptionVolume
                ? { priceOptionVolume: item.priceOptionVolume }
                : {}
            ),
        }));
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(itemsToSave));
      }
    }

    clearCart() {
      this.setCart(null);
      localStorage.removeItem(GUEST_CART_KEY);
    }

    // Async Actions
    async fetchCart() {
      this.setLoading(true);
        try {
          const { data } = await $api.get<ICart>('/cart');
          runInAction(() => this.setCart(data));
        } catch (e: any) {
            runInAction(() => this.setError(e.response?.data?.message || 'Nie udało się pobrać koszyka'));
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }

    async addItem(
        productId: number,
        priceOptionVolume?: string | null
    ) {
        this.setError(null);
    
        if (this.isAuth) {
            try {
                await $api.post('/cart', {
                    productId,
                    ...(priceOptionVolume
                        ? { priceOptionVolume }
                        : {}),
                });
    
                await this.fetchCart();
            } catch (e: any) {
                runInAction(() => {
                    this.setError(
                        e.response?.data?.message ||
                        'Nie udało się dodać produktu do koszyka.'
                    );
                });
    
                throw e;
            }
    
            return;
        }
    
        // Guest cart
        const existingItem = this.cartProducts.find(item =>
            this.isSameCartItem(
                item,
                productId,
                priceOptionVolume
            )
        );
    
        if (existingItem) {
            await this.updateItemQuantity(
                productId,
                existingItem.quantity + 1,
                priceOptionVolume
            );
    
            return;
        }
    
        this.setLoading(true);
    
        try {
            const { data: product } = await $api.get<IProduct>(
                `/products/${productId}`
            );
    
            const newCartItem: ICartItem = {
                id: Date.now(),
                quantity: 1,
                cartId: 0,
                productId: product.id,
                priceOptionVolume: priceOptionVolume ?? null,
                product,
                price: this.getEffectivePrice(
                    product,
                    priceOptionVolume
                ),
            };
    
            runInAction(() => {
                const currentProducts = this.cartProducts;
    
                this.setCart({
                    id: 0,
                    userId: 0,
                    cartProducts: [
                        ...currentProducts,
                        newCartItem,
                    ],
                });
    
                this.saveGuestCart();
            });
        } catch (e) {
            console.error(
                'Could not fetch product for guest cart',
                e
            );
    
            this.setError(
                'Nie można dodać produktu do koszyka.'
            );
        } finally {
            this.setLoading(false);
        }
    }

    async removeItem(
        productId: number,
        priceOptionVolume?: string | null
    ) {
        if (this.isAuth) {
            if (!this._cart) return;
    
            const itemIndex = this._cart.cartProducts.findIndex(
                item =>
                    this.isSameCartItem(
                        item,
                        productId,
                        priceOptionVolume
                    )
            );
    
            if (itemIndex === -1) return;
    
            const removedItem =
                this._cart.cartProducts[itemIndex];
    
            runInAction(() => {
                this._cart!.cartProducts.splice(itemIndex, 1);
            });
    
            try {
                await $api.delete(`/cart/${productId}`, {
                    params: priceOptionVolume
                        ? { priceOptionVolume }
                        : undefined,
                });
            } catch (e: any) {
                runInAction(() => {
                    this._cart!.cartProducts.splice(
                        itemIndex,
                        0,
                        removedItem
                    );
    
                    this.setError(
                        e.response?.data?.message ||
                        'Nie udało się usunąć produktu.'
                    );
                });
            }
    
            return;
        }
    
        // Guest cart
        if (!this._cart) return;
    
        const updatedProducts = this.cartProducts.filter(
            item =>
                !this.isSameCartItem(
                    item,
                    productId,
                    priceOptionVolume
                )
        );
    
        runInAction(() => {
            this.setCart({
                id: 0,
                userId: 0,
                cartProducts: updatedProducts,
            });
    
            this.saveGuestCart();
        });
    }

    async updateItemQuantity(
        productId: number,
        quantity: number,
        priceOptionVolume?: string | null
    ) {
        if (this.isAuth) {
            if (!this._cart) return;
    
            const itemIndex =
                this._cart.cartProducts.findIndex(
                    item =>
                        this.isSameCartItem(
                            item,
                            productId,
                            priceOptionVolume
                        )
                );
    
            if (itemIndex === -1) return;
    
            const originalQuantity =
                this._cart.cartProducts[itemIndex].quantity;
    
            runInAction(() => {
                this._cart!.cartProducts[itemIndex].quantity =
                    quantity;
            });
    
            try {
                await updateQuantity(
                    productId,
                    quantity,
                    priceOptionVolume
                );
            } catch (e: any) {
                runInAction(() => {
                    this._cart!.cartProducts[itemIndex].quantity =
                        originalQuantity;
    
                    this.setError(
                        e.response?.data?.message ||
                        'Błąd aktualizacji koszyka.'
                    );
                });
            }
    
            return;
        }
    
        if (quantity < 1) {
            await this.removeItem(
                productId,
                priceOptionVolume
            );
    
            return;
        }
    
        const item = this.cartProducts.find(item =>
            this.isSameCartItem(
                item,
                productId,
                priceOptionVolume
            )
        );
    
        if (item) {
            runInAction(() => {
                item.quantity = quantity;
                this.saveGuestCart();
            });
        }
    }

    async mergeAndFetchCart() {
        const guestCartJson =
            localStorage.getItem(GUEST_CART_KEY);
    
        if (guestCartJson) {
            try {
                const guestCartItems: GuestCartStorageItem[] =
                    JSON.parse(guestCartJson);
    
                if (guestCartItems.length > 0) {
                    await $api.post('/cart/merge', {
                        items: guestCartItems.map(item => ({
                            productId: item.productId,
                            quantity: item.quantity,
                            ...(item.priceOptionVolume
                                ? {
                                    priceOptionVolume:
                                        item.priceOptionVolume,
                                }
                                : {}),
                        })),
                    });
                }
            } catch (e) {
                console.error(
                    'Failed to merge guest cart',
                    e
                );
            } finally {
                localStorage.removeItem(GUEST_CART_KEY);
            }
        }
    
        await this.fetchCart();
    }
    
}

